import { useCallback, useEffect, useState } from 'react';
import { ErrorInfo, isErrorInfo } from '../../../../shared/interfaces/error-info.interface';
import { describeLoadError } from '../../../../shared/components/core-ui/load-error/load-error.component';
import { OpsFilterModel } from '../../../../shared/models/lead-ops.model';

/**
 * Loads one section's report and keeps its own loading and error state.
 *
 * Deliberately one hook per section rather than one fetch for the page: a
 * section that fails shows an error in its own card and can be retried there,
 * and everything that loaded stays readable. It also means a slow aggregation
 * does not hold up the fast ones.
 */
export function useOpsReport<T>(
  load: (filter: OpsFilterModel) => Promise<T | ErrorInfo>,
  filter: OpsFilterModel
): { data?: T; loading: boolean; error?: string; reload: () => void } {
  const [data, setData] = useState<T | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | undefined>();
  // The filter is rebuilt on every render of the parent, so it is compared by
  // value — depending on the object identity would refetch on every keystroke
  // anywhere on the page.
  const key = JSON.stringify(filter);

  const run = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    load(JSON.parse(key) as OpsFilterModel).then((result: T | ErrorInfo) => {
      if (cancelled) return;
      if (isErrorInfo(result as ErrorInfo)) {
        setError(describeLoadError(result as ErrorInfo));
        setData(undefined);
      } else {
        setError(undefined);
        setData(result as T);
      }
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => run(), [run]);

  return { data, loading, error, reload: run };
}
